import { Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DeleteDraftButtonProps {
  leagueName: string;
  onClick: () => void;
  /** Borderless in a dashboard row; outlined beside Edit league in the header. */
  variant: "row" | "header";
}

const VARIANT_CLASS: Record<DeleteDraftButtonProps["variant"], string> = {
  row: "size-8 rounded-lg border border-transparent",
  header:
    "size-9.5 rounded-xl border border-border bg-background hover:border-danger/20",
};

/**
 * Trash icon for a draft league. It sits inside the dashboard row, which is
 * itself a button that opens the league, so every event it handles is kept
 * from reaching the row: a click would open the league behind the dialog, and
 * Enter or Space would be taken by the row's key handler before the button
 * ever saw a click.
 */
export function DeleteDraftButton({
  leagueName,
  onClick,
  variant,
}: DeleteDraftButtonProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label={`Delete draft ${leagueName}`}
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className={cn(
                "flex shrink-0 items-center justify-center text-text-muted outline-none transition-colors duration-150",
                "hover:bg-danger/10 hover:text-danger focus-visible:bg-danger/10 focus-visible:text-danger focus-visible:ring-3 focus-visible:ring-danger/30",
                VARIANT_CLASS[variant],
              )}
            />
          }
        >
          <Trash2 className="size-4" aria-hidden="true" />
        </TooltipTrigger>
        <TooltipContent>Delete draft</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
